import { useTranslation, Translation } from 'react-i18next';
import React, { useMemo, memo, useCallback, ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import cx from 'clsx';
import ReactGA4 from 'react-ga4';

import {
  // IconEmail1,
  IconTwitter,
  // IconEmailWhite,
  IconTwitterWhite,
  IconDiscord,
  IconTelegram,
  IconRcSnsEyeClosed,
} from 'assets/sns';
import { useTheme, useDeviceTypeIsMobile, useUserType } from 'hooks';
import { uppercaseFirstLetter } from '@/utils';

import style from './index.module.css';
import { HelperTooltip } from '@/components';

const getSnsList = (isWhite: boolean) => [
  {
    label: <Translation>{t => t('SocialTag_SnsTag.cditeb')}</Translation>,
    value: (info: UserInfo) => info.twitter_id && `@${info.twitter_id}`,
    icon: isWhite ? IconTwitterWhite : IconTwitter,
    prefix: 'https://x.com',
    key: 'twitter',
  },
  {
    label: 'Discord',
    value: (info: UserInfo) => info.discord_id && `@${info.discord_id}`,
    icon: IconDiscord,
    key: 'discord',
  },
  {
    label: 'Telegram',
    value: (info: UserInfo) => info.telegram_id && `@${info.telegram_id}`,
    icon: IconTelegram,
    key: 'telegram',
  },
  // {
  //   label: <Translation>{t => t('SocialTag_SnsTag.13xh18')}</Translation>,
  //   key: 'email' as const,
  //   value: (info: UserInfo) => info.email,
  //   key: 'email' as const,
  //   icon: isWhite ? IconEmailWhite : IconEmail1,
  // },
];

type SnsItemProps = {
  label: ReactNode;
  icon: string;
  key: string;
  value?: string | null;
  isCurrentUser: boolean;
  prefix?: string;
  id?: string;
  gaCategory?: string;
  isHidden?: boolean;
};

const SnsItem = memo(
  ({ icon, key, value, prefix, isCurrentUser, gaCategory, isHidden }: SnsItemProps) => {
    const { t } = useTranslation();

    const isMobile = useDeviceTypeIsMobile();
    const userType = useUserType();

    const clickGA = useCallback(() => {
      ReactGA4.event({
        category: 'Social Click',
        action: `Click_${uppercaseFirstLetter(key)}`,
      });
    }, [key]);

    const handleClickAdd = useCallback(() => {
      if (gaCategory) {
        ReactGA4.event({
          category: gaCategory,
          action: `Icon_Add ${uppercaseFirstLetter(key)}_${userType}`,
        });
      }
    }, [gaCategory, key, userType]);

    if (value === null) {
      return isCurrentUser ? (
        <NavLink
          to="/credit"
          onClick={handleClickAdd}
          className={cx(style.tag, style.snsVerifyLink, isMobile && style.noBg)}>
          <img src={icon} alt="" className={style.icon} />
          {t('SocialTag_SnsTag.1eo1')}
        </NavLink>
      ) : null;
    }

    return (
      <HelperTooltip
        title={
          value ||
          (key === 'email'
            ? t('SocialTag_SnsTag.email-verified')
            : 'Due to privacy settings, the added account is not visible.')
        }>
        {prefix ? (
          <a
            className={cx(style.tag, style.snsLink, isMobile && style.noBg)}
            href={`${prefix}/${value?.replace(/^@/, '')}`}
            onClick={clickGA}
            target="_blank"
            rel="noreferrer">
            <img src={icon} alt="" className={style.icon} />
            {isHidden ? (
              <div className={style.eyeClosed}>
                <IconRcSnsEyeClosed />
              </div>
            ) : null}
          </a>
        ) : (
          <div className={cx(style.tag, isMobile && style.noBg)}>
            <img src={icon} alt="" className={style.icon} />
            {isHidden ? (
              <div className={style.eyeClosed}>
                <IconRcSnsEyeClosed />
              </div>
            ) : null}
          </div>
        )}
      </HelperTooltip>
    );
  },
);

type SnsListProps = {
  addrInfo: UserInfo; // 查看的人
  isCurrentUser: boolean;
  gaCategory?: string;
};

export const SnsList = memo(({ addrInfo, isCurrentUser, gaCategory }: SnsListProps) => {
  const { isDark } = useTheme();
  const isMobile = useDeviceTypeIsMobile();

  const snsList = useMemo(() => getSnsList(isDark || isMobile), [isDark, isMobile]);

  return (
    <>
      {snsList.map(x => (
        <SnsItem
          isCurrentUser={isCurrentUser}
          key={x.key}
          icon={x.icon}
          label={x.label}
          isHidden={!addrInfo[`is_${x.key}_public` as keyof UserInfo]}
          prefix={x.prefix}
          value={x.value && x.value(addrInfo)}
          gaCategory={gaCategory}
        />
      ))}
    </>
  );
});

export default SnsList;
